import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface ReportSummaryResponse {
  report_id: string; report_type: string;
  period_start: string; period_end: string;
  generated_at: string; approved: boolean;
}
interface ListReportsResponse { reports: ReportSummaryResponse[]; total: number; }

// ── Constants ─────────────────────────────────────────────────────────────────

export const REPORT_TYPES = ['daily', 'weekly', 'monthly'];

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function reportTypeHe(t: string): string {
  if (t === 'daily') return 'Daily';
  if (t === 'weekly') return 'Weekly';
  if (t === 'monthly') return 'Monthly';
  return t;
}

const TYPE_COLORS: Record<string, string> = {
  daily: '#7dd3fc',
  weekly: '#a78bfa',
  monthly: '#6ee7b7',
};

function reportTypeColor(t: string): string {
  return TYPE_COLORS[t] ?? 'var(--omega-ink-subtle)';
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceReportsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [typeFilter, setTypeFilter] = useState('');
  const [periodStart, setPeriodStart] = useState('');

  const params = new URLSearchParams();
  if (typeFilter) params.set('type', typeFilter);
  if (periodStart) params.set('period_start', periodStart);
  const qs = params.toString();

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'reports', 'list', typeFilter, periodStart],
    queryFn: () => apiFetch<ListReportsResponse>(`/finance/reports${qs ? '?' + qs : ''}`),
    refetchInterval: 30_000,
  });

  const reports = data?.reports ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Reports' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Reports
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {reports.length}{reports.length !== data.total ? `/${data.total}` : ''}
          </span>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={inputStyle}>
          <option value="">All types</option>
          {REPORT_TYPES.map(t => <option key={t} value={t}>{reportTypeHe(t)}</option>)}
        </select>
        <input
          type="text"
          value={periodStart}
          onChange={e => setPeriodStart(e.target.value)}
          placeholder="Period start (2026-05)"
          style={{ ...inputStyle, minWidth: 180 }}
        />
        {(typeFilter || periodStart) && (
          <button
            onClick={() => { setTypeFilter(''); setPeriodStart(''); }}
            style={{ ...inputStyle, cursor: 'pointer', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Column headers */}
      {!isPending && !isError && reports.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '80px 1fr 1fr 160px 80px',
          gap: 10,
          padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          {['Type', 'Start', 'End', 'Created', 'Status'].map(h => (
            <span key={h} style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)' }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {!isPending && !isError && reports.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
          No reports to display.
        </div>
      )}

      {reports.map(r => (
        <Link
          key={r.report_id}
          to={`/omega/finance/reports/${r.report_id}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '80px 1fr 1fr 160px 80px',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            textDecoration: 'none',
            color: 'inherit',
          }}
          onMouseEnter={ev => { (ev.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.03)'; }}
          onMouseLeave={ev => { (ev.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <span style={{
            fontSize: '7.5pt', padding: '1px 6px',
            border: `1px solid ${reportTypeColor(r.report_type)}`,
            color: reportTypeColor(r.report_type),
            letterSpacing: '0.06em', fontWeight: 700, borderRadius: 3,
            justifySelf: 'start', whiteSpace: 'nowrap',
          }}>
            {reportTypeHe(r.report_type)}
          </span>
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {r.period_start}
          </span>
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {r.period_end}
          </span>
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {fmtDate(r.generated_at)}
          </span>
          <span style={{
            fontSize: '7.5pt', padding: '1px 6px',
            border: r.approved ? '1px solid rgba(52,211,153,0.5)' : '1px solid rgba(251,191,36,0.5)',
            color: r.approved ? '#34d399' : '#fbbf24',
            letterSpacing: '0.06em', fontWeight: 700, borderRadius: 3,
            justifySelf: 'start', whiteSpace: 'nowrap',
          }}>
            {r.approved ? '✓ Approved' : 'Pending'}
          </span>
        </Link>
      ))}
    </div>
  );
}
